// emailAuthService.ts - 이메일 기반 인증
import { User } from '@/types/app_types';
import axiosInstance, { ApiError } from '@/utils/axios';
import { tokenService } from '@/utils/tokenService';
import { logger } from '@/utils/logger';
import { InternalAxiosRequestConfig } from 'axios';
import {
  AuthResponse,
  RegistrationResponse,
  StandardApiResponse,
  SignupData,
  AuthResult,
} from '@/types/auth_types';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipErrorSnackbar?: boolean;
}

interface VerificationResponse {
  success: boolean;
  message: string;
  data?: {
    confirm_code?: string;
  };
}

/**
 * 이메일/비밀번호 로그인
 */
export const login = async (
  email: string,
  password: string,
): Promise<StandardApiResponse<AuthResult>> => {
  try {
    const { data } = await axiosInstance.post<AuthResponse>(
      '/auth/login/',
      { email, password },
      {
        skipErrorSnackbar: true,
      } as ExtendedAxiosRequestConfig,
    );

    const user: User = {
      id: data.user.uid.toString(),
      email: data.user.email,
      name: data.user.full_name || data.user.email.split('@')[0],
      provider: 'email',
      walletAddress: null,
      ensName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      profile: {
        nftsOwned: 0,
        totalPurchases: 0,
      },
    };

    tokenService.setTokens({
      accessToken: data.access,
      refreshToken: data.refresh,
    });

    return {
      success: true,
      message: data.message || '로그인에 성공했습니다.',
      data: {
        user,
        tokens: {
          accessToken: data.access,
          refreshToken: data.refresh,
        },
      },
    };
  } catch (error) {
    const apiError = error as ApiError;

    let errorMessage = '로그인 중 오류가 발생했습니다. 다시 시도해 주세요.';

    if (apiError.errors?.non_field_errors && Array.isArray(apiError.errors.non_field_errors)) {
      errorMessage = apiError.errors.non_field_errors[0];
    } else if (apiError.message) {
      errorMessage = apiError.message;
    }

    return {
      success: false,
      message: errorMessage,
      data: null,
    };
  }
};

/**
 * 이메일 중복 체크
 */
export const checkEmailDuplicate = async (email: string) => {
  const { data } = await axiosInstance.post('/auth/check-email/', { email });
  return data;
};

/**
 * 비밀번호 리셋 이메일 발송
 */
export const resetPassword = async (email: string) => {
  const { data } = await axiosInstance.post('/auth/password/reset/', { email });
  return data;
};

/**
 * 비밀번호 리셋 확인 - 토큰 기반
 */
export const confirmResetPassword = async (
  uid: string,
  token: string,
  new_password1: string,
  new_password2: string,
) => {
  const { data } = await axiosInstance.post('/auth/password/reset/confirm/', {
    uid,
    token,
    new_password1,
    new_password2,
  });
  return data;
};

/**
 * 이메일 인증번호 발송
 */
export const sendVerificationCode = async (
  email: string,
): Promise<StandardApiResponse<{ code?: string }>> => {
  const { data } = await axiosInstance.post<VerificationResponse>('/auth/send-confirm-code/', {
    email,
  });

  return {
    success: data.success,
    message: data.message || '인증번호가 발송되었습니다.',
    data: {
      code: data.data?.confirm_code,
    },
  };
};

/**
 * 이메일 인증번호 확인
 * POST /auth/verify-confirm-code/
 */
export const verifyConfirmCode = async (
  email: string,
  code: string,
): Promise<StandardApiResponse<null>> => {
  try {
    const { data } = await axiosInstance.post<{
      success: boolean;
      message: string;
      code?: string;
    }>('/auth/verify-confirm-code/', {
      email,
      code,
    });

    return {
      success: data.success,
      message: data.message,
      data: null,
    };
  } catch (error) {
    const apiError = error as ApiError;

    let errorMessage = '인증에 실패했습니다. 다시 시도해 주세요.';
    const errorCode = apiError.code;

    if (errorCode === 'EXPIRED_CODE') {
      errorMessage = '인증번호가 만료되었습니다. 인증번호를 다시 발송해 주세요.';
    } else if (errorCode === 'INVALID_CODE') {
      errorMessage = '인증번호가 올바르지 않습니다. 다시 확인해 주세요.';
    } else if (errorCode === 'EMAIL_NOT_FOUND') {
      errorMessage = '해당 이메일로 발송된 인증번호가 없습니다.';
    } else if (apiError.message) {
      errorMessage = apiError.message;
    }

    return {
      success: false,
      message: errorMessage,
      data: null,
      errorCode,
    };
  }
};

/**
 * 이메일 인증번호 수령 후 회원가입 요청
 * 응답 형식:
 * 성공: { "email": "...", "full_name": "...", "confirm_code": "..." }
 * 오류(중복): { "email": ["이미 사용 중인 이메일입니다."] }
 */
export const signup = async (signupData: SignupData): Promise<StandardApiResponse<AuthResult>> => {
  try {
    const response = await axiosInstance.post<RegistrationResponse>('/auth/registration/', signupData, {
      skipErrorSnackbar: true,
    } as ExtendedAxiosRequestConfig);

    const { data } = response;
    const registrationData = data.user;

    // 회원가입 응답에 uid가 없으므로 임시 ID 생성
    const user: User = {
      id: `temp_${Date.now()}`,
      email: registrationData.email,
      name: registrationData.full_name,
      provider: 'email',
      walletAddress: null,
      ensName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      profile: {
        nftsOwned: 0,
        totalPurchases: 0,
      },
    };

    return {
      success: true,
      message: '회원가입이 완료되었습니다. 로그인해 주세요.',
      data: {
        user,
      },
    };
  } catch (error) {
    logger.error('회원가입 API 오류', { error });
    const apiError = error as ApiError;

    let errorMessage = '회원가입 중 오류가 발생했습니다. 다시 시도해 주세요.';
    const errorCode = apiError.code;

    // 에러 코드 기반 처리
    if (errorCode === 'EXPIRED_CODE') {
      errorMessage = '인증번호가 만료되었습니다. 인증번호를 다시 발송해 주세요.';
    } else if (errorCode === 'INVALID_CODE') {
      errorMessage = '인증번호가 올바르지 않습니다. 다시 확인해 주세요.';
    } else if (errorCode === 'EMAIL_NOT_FOUND') {
      errorMessage = '해당 이메일로 발송된 인증번호가 없습니다.';
    } else if (apiError.errors?.email && Array.isArray(apiError.errors.email)) {
      errorMessage = apiError.errors.email[0];
    } else if (apiError.errors?.non_field_errors && Array.isArray(apiError.errors.non_field_errors)) {
      errorMessage = apiError.errors.non_field_errors[0];
    } else if (apiError.message) {
      errorMessage = apiError.message;
    }

    return {
      success: false,
      message: errorMessage,
      data: null,
      errorCode,
    };
  }
};

/**
 * 로그아웃
 */
export const logout = async () => {
  const { data } = await axiosInstance.post('/auth/logout/');
  return data;
};
